/**
 * Sky & sun model for the rig scene.
 *
 * The sun is computed astronomically from the site's coordinates and a local (Europe/Oslo) date/time, so shadows fall
 * the way they will on the real site. An HDRI from the vendored CC0 set (see assets3d.manifest.json) is then chosen by
 * weather and by the measured sun elevation closest to the computed one, and rotated about the vertical axis so the
 * sun in the sky image sits exactly where the directional light comes from. Everything here is deterministic.
 */
import manifest from "./assets3d.manifest.json";

export const SEASONS = [
  { value: "vinter", label: "Vinter", month: 1, day: 15 },
  { value: "vaar", label: "Vår", month: 4, day: 20 },
  { value: "sommer", label: "Sommer", month: 6, day: 21 },
  { value: "host", label: "Høst", month: 10, day: 1 },
];
export const WEATHERS = [
  { value: "klart", label: "Klart" },
  { value: "delvis", label: "Delvis skyet" },
  { value: "overskyet", label: "Overskyet" },
];
export const TIME_PRESETS = [
  { label: "Morgen", hour: 8 },
  { label: "Middag", hour: 12.5 },
  { label: "Ettermiddag", hour: 15.5 },
  { label: "Kveld", hour: 20.5 },
  { label: "Natt", hour: 23.5 },
];
export const DEFAULT_SKY = { hour: 14, season: "sommer", weather: "delvis" };
/** Fallback site when the rig has no coordinates: Haugesund (Stord Innkvartering's home region). */
export const DEFAULT_SITE = { lat: 59.41, lng: 5.27 };

const DEG = Math.PI / 180;

/** Minutes east of UTC for a zone at the given instant (Intl-based, DST-aware). */
function tzOffsetMinutes(date, tz = "Europe/Oslo") {
  try {
    const f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const p = Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch {
    const m = date.getUTCMonth();
    return m >= 2 && m <= 9 ? 120 : 60;
  }
}

/** Local Oslo wall-clock (year, month 1-12, day, decimal hour) → UTC Date. */
export function osloToUTC(year, month, day, hour) {
  const h = Math.floor(hour), min = Math.round((hour - h) * 60);
  let guess = new Date(Date.UTC(year, month - 1, day, h, min));
  const off = tzOffsetMinutes(guess);
  guess = new Date(guess.getTime() - off * 60000);
  const off2 = tzOffsetMinutes(guess);
  if (off2 !== off) guess = new Date(Date.UTC(year, month - 1, day, h, min) - off2 * 60000);
  return guess;
}

/**
 * Solar azimuth (deg, from north through east) and elevation (deg) – Astronomical Almanac low-precision algorithm
 * (accuracy ≈ 0.01°, more than enough for lighting).
 */
export function solarPosition(lat, lng, dateUTC) {
  const d = dateUTC.getTime() / 86400000 + 2440587.5 - 2451545.0; // days since J2000.0
  const g = ((357.529 + 0.98560028 * d) % 360) * DEG; // mean anomaly
  const q = (280.459 + 0.98564736 * d) % 360; // mean longitude
  const L = ((q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) % 360) * DEG; // ecliptic longitude
  const e = (23.439 - 0.00000036 * d) * DEG; // obliquity
  const RA = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)); // rad
  const dec = Math.asin(Math.sin(e) * Math.sin(L));
  const gmst = ((18.697374558 + 24.06570982441908 * d) % 24) * 15; // deg
  let ha = ((gmst + lng - RA / DEG) % 360 + 540) % 360 - 180; // hour angle deg in [-180,180)
  ha *= DEG;
  const la = lat * DEG;
  const el = Math.asin(Math.sin(la) * Math.sin(dec) + Math.cos(la) * Math.cos(dec) * Math.cos(ha));
  const az = Math.atan2(-Math.sin(ha), Math.tan(dec) * Math.cos(la) - Math.sin(la) * Math.cos(ha));
  return { azimuth: ((az / DEG) + 360) % 360, elevation: el / DEG };
}

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const lerp = (a, b, t) => a + (b - a) * t;
function mixHex(a, b, t) {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16)), pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return "#" + pa.map((v, i) => Math.round(lerp(v, pb[i], t)).toString(16).padStart(2, "0")).join("");
}

const HDRIS = manifest.hdri;
const byId = (id) => HDRIS.find((h) => h.id === id);
const NIGHT = byId("qwantani_night_puresky");
const TWILIGHT = byId("qwantani_dusk_1_puresky");
const OVERCAST = byId("rogland_overcast");

/**
 * Resolve everything the scene needs for a sky setting at a site.
 * @returns {{sun:{azimuth,elevation}, hdri, rotationY, envIntensity, backgroundIntensity, sunIntensity, sunColor, exposure, fog, ambient, isNight, label}}
 */
export function resolveSky(sky = DEFAULT_SKY, site = DEFAULT_SITE, year = new Date().getFullYear()) {
  const season = SEASONS.find((s) => s.value === sky.season) || SEASONS[2];
  const lat = Number.isFinite(site?.lat) ? site.lat : DEFAULT_SITE.lat;
  const lng = Number.isFinite(site?.lng) ? site.lng : DEFAULT_SITE.lng;
  const when = osloToUTC(year, season.month, season.day, sky.hour ?? 14);
  const sun = solarPosition(lat, lng, when);
  const el = sun.elevation;
  const weather = sky.weather || "delvis";
  const isNight = el < -6;

  // pick the HDRI: weather family, then nearest measured sun elevation
  let hdri;
  if (isNight) hdri = NIGHT;
  else if (weather === "overskyet") hdri = OVERCAST;
  else if (el < 1) hdri = TWILIGHT;
  else {
    const family = HDRIS.filter((h) => h.sun && h.sun.pixels > 1 && (h.weather === weather || (weather === "delvis" && h.weather === "klart" && h.sun.elevation < 20) || (weather === "klart" && h.weather === "delvis" && false)));
    const pool = family.length ? family : HDRIS.filter((h) => h.sun && h.sun.pixels > 1);
    hdri = pool.reduce((best, h) => (Math.abs(h.sun.elevation - el) < Math.abs(best.sun.elevation - el) ? h : best), pool[0]);
  }

  // rotate the sky so its sun sits at the computed azimuth (three.js equirect: u = atan2(z, x) / 2π + 0.5)
  let rotationY = 0;
  if (hdri?.sun && hdri.sun.pixels > 1) {
    const thetaImg = (hdri.sun.u - 0.5) * Math.PI * 2;
    const A = sun.azimuth * DEG;
    const thetaWorld = Math.atan2(-Math.cos(A), Math.sin(A));
    rotationY = thetaWorld - thetaImg;
  }

  // direct sun: strength and warmth from elevation; overcast = soft, cool sky light only.
  // Sunlit/shade contrast is what sells daylight, so the sun is strong and the sky dome (which already contains the sun
  // in the HDRI) is held back in clear weather – real-world ratios are 5:1 or more.
  const daylight = clamp01((el + 3) / 18); // 0 at −3° (sun gone), 1 from 15° up
  const twilight = clamp01((el + 6) / 7); // 0 at −6° (night), 1 at +1° – dims the dusk/dawn skies
  const lightsOn = el < 1; // windows glow from the blue hour on
  let sunIntensity = 0, sunColor = "#fff4e6";
  if (isNight) { sunIntensity = 0.14; sunColor = "#b9c7e6"; }
  else if (weather === "overskyet") { sunIntensity = 0.5 * clamp01((el + 6) / 20); sunColor = "#e6ebf2"; }
  else {
    sunIntensity = (weather === "delvis" ? 3.2 : 3.6) * Math.pow(daylight, 0.85);
    sunColor = mixHex("#ffa860", "#fff4e6", clamp01((el - 1) / 30));
  }
  const dusk = lerp(0.45, 1, twilight);
  const envIntensity = isNight ? 0.35 : weather === "overskyet" ? 1.0 * dusk : (weather === "delvis" ? lerp(0.7, 0.62, daylight) : lerp(0.72, 0.55, daylight)) * dusk;
  const exposure = isNight ? 1.3 : (weather === "overskyet" ? 1.0 : lerp(1.2, 1.0, daylight)) * lerp(0.8, 1, twilight);
  // shadow softness (directional light "size" in metres): crisp in clear sun, softer with cloud, very soft when overcast
  const shadowSize = weather === "overskyet" ? 12 : weather === "delvis" ? 4.5 : 3;
  const fog = hdri?.horizon || "#b9c3cf";
  const label = `${hdri?.label || "Himmel"} · sol ${el.toFixed(0)}° · ${compass(sun.azimuth)}`;
  return { sun, hdri, rotationY, envIntensity, backgroundIntensity: 1, sunIntensity, sunColor, exposure, shadowSize, fog, isNight, lightsOn, weather, label, when };
}

export function compass(az) {
  const names = ["N", "NNØ", "NØ", "ØNØ", "Ø", "ØSØ", "SØ", "SSØ", "S", "SSV", "SV", "VSV", "V", "VNV", "NV", "NNV"];
  return names[Math.round((((az % 360) + 360) % 360) / 22.5) % 16];
}

/** Direction *towards* the sun in scene space (north = −z, east = +x). */
export function sunDirection(azimuthDeg, elevationDeg) {
  const A = azimuthDeg * DEG, E = elevationDeg * DEG;
  return [Math.sin(A) * Math.cos(E), Math.sin(E), -Math.cos(A) * Math.cos(E)];
}

export const fmtHour = (h) => `${String(Math.floor(h)).padStart(2, "0")}:${String(Math.round((h % 1) * 60)).padStart(2, "0")}`;

/* ---------------- rendering quality tiers ---------------- */

/**
 * «Lav» keeps the scene light (no soft shadows, half shadow map, no contact shadows, DPR 1); «Normal» is the default –
 * HDRI sky, photo textures, soft sun shadows, contact shadows; «Høy» adds the post-processing stack (ambient
 * occlusion, bloom, SMAA, vignette) and must be chosen actively.
 */
export const QUALITIES = [
  { value: "lav", label: "Lav", hint: "Rask – enkle skygger" },
  { value: "normal", label: "Normal", hint: "HDRI-himmel, myke skygger" },
  { value: "hoy", label: "Høy", hint: "+ ambient occlusion, etterbehandling" },
];
const QUALITY_MIGRATE = { auto: "normal", standard: "lav", hoy: "hoy", lav: "lav", normal: "normal" };
/** Effective tier for the current device: stored preference, mapped from older values; phones never go above «Normal». */
export function effectiveQuality(pref) {
  const q = QUALITY_MIGRATE[pref] || "normal";
  const small = typeof window !== "undefined" && window.innerWidth < 768;
  if (small && q === "hoy") return "normal";
  return q;
}
export const isKnownQuality = (q) => ["lav", "normal", "hoy"].includes(q);
